// The lesson-builder page's overlay/popup renders -- a pure move, same
// props, same conditions, same markup as before. See
// docs/BLUEPRINT-2026-10-03-lesson-builder-decomposition.md for why this
// is split from lesson-builder/page.tsx and what its boundary is.

import { Lesson, Class } from '@all-for-one/shared-types'
import { NavigateFunction } from 'react-router-dom'
import { AssignClassOverlay } from 'lesson-builder/assign-class-overlay'
import { LaunchLessonOverlay } from 'lesson-builder/components/launch-lesson-overlay'
import { StudentLoginsOverlay } from 'lesson-builder/student-logins-overlay'
import { CopyToClassOverlay } from 'lesson-builder/copy-to-class-overlay'
import { PrintDraftOverlay } from 'lesson-builder/print-draft-overlay'
import { PrintAnswerKeyOverlay } from 'lesson-builder/print-answer-key-overlay'
import { PrintWorksheetsOverlay } from 'lesson-builder/print-worksheets-overlay'
import { LessonBuilderOverlaysState } from 'lesson-builder/use-lesson-builder-overlays'
import readyStyles from 'css/components/lesson-builder/ready-overlay.module.css'

interface LessonBuilderOverlaysProps {
  overlays: LessonBuilderOverlaysState
  lessonId: string | null
  lessonData: Lesson | undefined
  classData: Class | undefined
  assignedClass: { classId: string; name: string } | null
  name: string
  navigate: NavigateFunction
  onLaunchLesson: () => void
  setIsClassAssigned: (v: boolean) => void
  setAssignedClass: (c: { classId: string; name: string } | null) => void
}

export const LessonBuilderOverlays: React.FC<LessonBuilderOverlaysProps> = ({
  overlays,
  lessonId,
  lessonData,
  classData,
  assignedClass,
  name,
  navigate,
  onLaunchLesson,
  setIsClassAssigned,
  setAssignedClass,
}) => {
  const {
    showReadyPopup, setShowReadyPopup,
    showAssignClassOverlay, setShowAssignClassOverlay,
    showLaunchOverlay, setShowLaunchOverlay,
    showStudentLoginsOverlay, setShowStudentLoginsOverlay,
    copyOverlayMode, setCopyOverlayMode,
    showPrintDraftOverlay, setShowPrintDraftOverlay,
    showPrintAnswerKeyOverlay, setShowPrintAnswerKeyOverlay,
    showPrintWorksheetsOverlay, setShowPrintWorksheetsOverlay,
  } = overlays

  return (
    <>
      {showReadyPopup && (
        <div className="overlay">
          <div className={readyStyles.modal}>
            <div className={readyStyles.icon}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h2 className={readyStyles.title}>Lesson is ready!</h2>
            <p className={readyStyles.description}>Every question has been approved. You can launch now or come back to it later.</p>
            <div className={readyStyles.actions}>
              <button type="button" className={readyStyles.doneButton} onClick={() => setShowReadyPopup(false)}>Done</button>
              <button
                type="button"
                className={readyStyles.launchButton}
                onClick={() => {
                  setShowReadyPopup(false)
                  onLaunchLesson()
                }}
              >
                Launch lesson
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>
              </button>
            </div>
          </div>
        </div>
      )}
      {showAssignClassOverlay && (
        <AssignClassOverlay
          lessonId={lessonId}
          lessonGradeBand={lessonData?.gradeBand}
          onDismiss={() => setShowAssignClassOverlay(false)}
          setIsClassAssigned={setIsClassAssigned}
          setAssignedClass={setAssignedClass}
        />
      )}
      {showLaunchOverlay && (
        <LaunchLessonOverlay
          lessonName={name}
          lessonId={lessonId}
          className={assignedClass?.name}
          onDismiss={() => setShowLaunchOverlay(false)}
          onLaunch={() => {
            // Handle actual launch logic
            if (assignedClass?.classId && lessonId) {
              navigate(`/present/${assignedClass.classId}/${lessonId}`)
            }
          }}
          buttonTextA="Cancel"
          buttonTextB="Open"
        />
      )}
      {showStudentLoginsOverlay && classData && (
        <StudentLoginsOverlay
          classData={classData}
          onDismiss={() => setShowStudentLoginsOverlay(false)}
        />
      )}
      {copyOverlayMode && (
        <CopyToClassOverlay
          mode={copyOverlayMode}
          lessonId={lessonId!}
          lessonName={name}
          currentClassId={assignedClass?.classId || ''}
          currentGradeBand={lessonData?.gradeBand}
          onDismiss={() => setCopyOverlayMode(null)}
        />
      )}
      {showPrintDraftOverlay && (
        <PrintDraftOverlay
          lesson={lessonData!}
          className={assignedClass?.name}
          gradeBand={lessonData?.gradeBand}
          subject={lessonData?.subject}
          classData={classData}
          onDismiss={() => setShowPrintDraftOverlay(false)}
        />
      )}
      {showPrintAnswerKeyOverlay && (
        <PrintAnswerKeyOverlay
          lesson={lessonData!}
          className={assignedClass?.name}
          gradeBand={lessonData?.gradeBand}
          subject={lessonData?.subject}
          classData={classData}
          onDismiss={() => setShowPrintAnswerKeyOverlay(false)}
        />
      )}
      {showPrintWorksheetsOverlay && (
        <PrintWorksheetsOverlay
          lesson={lessonData!}
          assignedClassName={assignedClass?.name}
          gradeBand={lessonData?.gradeBand}
          subject={lessonData?.subject}
          classData={classData}
          onDismiss={() => setShowPrintWorksheetsOverlay(false)}
        />
      )}
    </>
  )
}
